import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MaintenanceScheduleDto, CreateMaintenanceScheduleDto, UpdateMaintenanceScheduleDto, ScheduleType, FrequencyUnit, WorkOrderTemplate, PriorityDto, StatusDto, User, EquipmentDto } from '../../types';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { formatDate } from '../../utils/formatters';
import { toast } from 'react-hot-toast';

interface MaintenanceScheduleFormProps {
	schedule?: MaintenanceScheduleDto | null;
	equipmentId?: number;
	onSubmit: (data: CreateMaintenanceScheduleDto | UpdateMaintenanceScheduleDto) => void;
	isLoading?: boolean;
	error?: string;
	onFormDirtyChange?: (isDirty: boolean) => void;
}

const maintenanceScheduleSchema = yup.object({
	equipmentId: yup.number().required('Equipment is required'),
	name: yup.string().required('Schedule name is required').max(200, 'Name must be less than 200 characters'),
	description: yup.string().max(1000, 'Description must be less than 1000 characters'),
	scheduleType: yup.number().required('Schedule type is required'),
	frequencyValue: yup.number().required('Frequency value is required').min(1, 'Frequency value must be at least 1'),
	frequencyUnit: yup.number().required('Frequency unit is required'),
	meterThreshold: yup.number().nullable().when('scheduleType', {
		is: ScheduleType.MeterBased,
		then: (schema) => schema.required('Meter threshold is required for meter-based schedules').min(1, 'Meter threshold must be at least 1'),
		otherwise: (schema) => schema.nullable(),
	}),
	currentMeterValue: yup.number().nullable().when('scheduleType', {
		is: ScheduleType.MeterBased,
		then: (schema) => schema.nullable().min(0, 'Current meter value cannot be negative'),
		otherwise: (schema) => schema.nullable(),
	}),
	nextDueDate: yup.string().required('Next due date is required'),
	autoGenerateWorkOrders: yup.boolean().required(),
	workOrderTemplateId: yup.number().nullable(),
	assignedUserId: yup.string().nullable(),
	defaultPriorityId: yup.number().nullable(),
	defaultStatusId: yup.number().nullable(),
	leadTimeDays: yup.number().required('Lead time is required').min(0, 'Lead time cannot be negative').max(365, 'Lead time cannot exceed 365 days'),
	isActive: yup.boolean().required(),
});

const MaintenanceScheduleForm: React.FC<MaintenanceScheduleFormProps> = ({ schedule, equipmentId, onSubmit, isLoading = false, error, onFormDirtyChange }) => {
	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors, isDirty },
		reset
	} = useForm<any>({
		resolver: yupResolver(maintenanceScheduleSchema),
		defaultValues: {
			equipmentId: schedule?.equipmentId || equipmentId || undefined,
			name: schedule?.name || '',
			description: schedule?.description || '',
			scheduleType: schedule?.scheduleType ?? ScheduleType.Calendar,
			frequencyValue: schedule?.frequencyValue || 1,
			frequencyUnit: schedule?.frequencyUnit ?? FrequencyUnit.Months,
			meterThreshold: schedule?.meterThreshold || undefined,
			currentMeterValue: schedule?.currentMeterValue || undefined,
			nextDueDate: schedule?.nextDueDate ? formatDate(schedule.nextDueDate, 'YYYY-MM-DD') : '',
			autoGenerateWorkOrders: schedule?.autoGenerateWorkOrders ?? true,
			workOrderTemplateId: schedule?.workOrderTemplateId || undefined,
			assignedUserId: schedule?.assignedUserId || undefined,
			defaultPriorityId: schedule?.defaultPriorityId || undefined,
			defaultStatusId: schedule?.defaultStatusId || undefined,
			leadTimeDays: schedule?.leadTimeDays ?? 0,
			isActive: schedule?.isActive ?? true,
		}
	});

	const scheduleType = watch('scheduleType');
	const autoGenerateWorkOrders = watch('autoGenerateWorkOrders');
	const [clientFilter, setClientFilter] = useState<number | 'all'>('all');
	const lastLeaveUrlRef = useRef<string | null>(null);
	const lastPromptAtRef = useRef<number>(0);
	const suppressBeforeUnloadRef = useRef<boolean>(false);

	// Notify parent component of form dirty state changes
	useEffect(() => {
		if (onFormDirtyChange) {
			onFormDirtyChange(isDirty);
		}
	}, [isDirty, onFormDirtyChange]);

	// Warn on browser/tab close or navigation while form has unsaved changes
	useEffect(() => {
		if (!isDirty) return;

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (suppressBeforeUnloadRef.current) return; // allow clean navigation without alert
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', handleBeforeUnload);

		// Intercept in-app link clicks (including Link from react-router which renders <a>)
		const handleDocumentClick = (e: MouseEvent) => {
			const target = e.target as Element | null;
			const anchor = target ? target.closest('a') as HTMLAnchorElement | null : null;
			if (!anchor) return;
			if (anchor.target === '_blank') return;
			if (!anchor.href) return;
			// Only same-origin navigations
			try {
				const url = new URL(anchor.href);
				if (url.origin !== window.location.origin) return;
				// Allow staying within the maintenance schedule routes
				const isMaintenanceScheduleRoute = url.pathname.startsWith('/maintenance-schedules');
				if (!isMaintenanceScheduleRoute) {
					e.preventDefault();
					e.stopPropagation();

					const now = Date.now();
					const sameUrl = lastLeaveUrlRef.current === url.pathname + url.search + url.hash;
					const withinWindow = now - lastPromptAtRef.current < 5000;

					if (sameUrl && withinWindow) {
						// proceed with navigation
						lastLeaveUrlRef.current = null;
						lastPromptAtRef.current = 0;
						suppressBeforeUnloadRef.current = true;
						window.location.assign(url.href);
						return;
					}

					lastLeaveUrlRef.current = url.pathname + url.search + url.hash;
					lastPromptAtRef.current = now;
					toast((t) => (
						<span className="text-sm">
							You have unsaved changes. Click the link again to leave.
						</span>
					), { id: 'schedule-unsaved-warning', duration: 4000, icon: '⚠️' });
				}
			} catch {}
		};
		document.addEventListener('click', handleDocumentClick, true);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			document.removeEventListener('click', handleDocumentClick, true);
		};
	}, [isDirty]);

	// Fetch equipment list
	const { data: equipmentResponse } = useQuery({
		queryKey: ['equipment', 1, 1000],
		queryFn: () => apiService.getEquipment(1, 1000),
	});

	const equipmentList = equipmentResponse?.data?.data || [];

	// Fetch clients for the filter
	const { data: clientsData } = useQuery({
		queryKey: ['clients-for-schedule-form'],
		queryFn: async () => {
			const clientsResponse = await apiService.getClients(1, 1000);
			return clientsResponse.data?.data || [];
		},
	});

	const clients = clientsData || [];

	// Filter equipment by selected client
	const filteredEquipmentList = useMemo(() => {
		if (clientFilter === 'all') {
			return equipmentList;
		}
		const selectedClient = clients.find(c => c.id === clientFilter);
		if (!selectedClient) {
			return equipmentList;
		}
		return equipmentList.filter((eq: EquipmentDto) => eq.clientName === selectedClient.name);
	}, [equipmentList, clientFilter, clients]);

	// When client filter changes, clear equipment selection if it doesn't match
	useEffect(() => {
		if (clientFilter !== 'all' && watch('equipmentId')) {
			const selectedEquipment = equipmentList.find((eq: EquipmentDto) => eq.id === watch('equipmentId'));
			const selectedClient = clients.find(c => c.id === clientFilter);
			if (selectedEquipment && selectedClient && selectedEquipment.clientName !== selectedClient.name) {
				setValue('equipmentId', undefined);
			}
		}
	}, [clientFilter, equipmentList, clients, watch, setValue]);

	// Fetch work order templates
	const { data: templatesResponse } = useQuery({
		queryKey: ['work-order-templates'],
		queryFn: () => apiService.getWorkOrderTemplates(),
	});

	const templates = templatesResponse?.data || [];

	// Fetch users
	const { data: usersResponse } = useQuery({
		queryKey: ['users'],
		queryFn: () => apiService.getUsers(1, 1000),
	});

	const users = usersResponse?.data?.data || [];

	// Fetch priorities
	const { data: prioritiesResponse } = useQuery({
		queryKey: ['priorities'],
		queryFn: () => apiService.getPriorities(),
	});

	const priorities = prioritiesResponse?.data || [];

	// Fetch statuses
	const { data: statusesResponse } = useQuery({
		queryKey: ['statuses'],
		queryFn: () => apiService.getStatuses(),
	});

	const statuses = statusesResponse?.data || [];

	// Reset form when schedule prop changes
	useEffect(() => {
		if (schedule) {
			reset({
				equipmentId: schedule.equipmentId,
				name: schedule.name,
				description: schedule.description || '',
				scheduleType: schedule.scheduleType,
				frequencyValue: schedule.frequencyValue,
				frequencyUnit: schedule.frequencyUnit,
				meterThreshold: schedule.meterThreshold || undefined,
				currentMeterValue: schedule.currentMeterValue || undefined,
				nextDueDate: schedule.nextDueDate ? formatDate(schedule.nextDueDate, 'YYYY-MM-DD') : '',
				autoGenerateWorkOrders: schedule.autoGenerateWorkOrders,
				workOrderTemplateId: schedule.workOrderTemplateId || undefined,
				assignedUserId: schedule.assignedUserId || undefined,
				defaultPriorityId: schedule.defaultPriorityId || undefined,
				defaultStatusId: schedule.defaultStatusId || undefined,
				leadTimeDays: schedule.leadTimeDays,
				isActive: schedule.isActive,
			});
		} else if (equipmentId) {
			reset({
				equipmentId: equipmentId,
				name: '',
				description: '',
				scheduleType: ScheduleType.Calendar,
				frequencyValue: 1,
				frequencyUnit: FrequencyUnit.Months,
				meterThreshold: undefined,
				currentMeterValue: undefined,
				nextDueDate: '',
				autoGenerateWorkOrders: true,
				workOrderTemplateId: undefined,
				assignedUserId: undefined,
				defaultPriorityId: undefined,
				defaultStatusId: undefined,
				leadTimeDays: 0,
				isActive: true,
			});
		}
	}, [schedule, equipmentId, reset]);

	const handleFormSubmit = (data: any) => {
		// Clean up the data
		const cleanedData: CreateMaintenanceScheduleDto | UpdateMaintenanceScheduleDto = {
			...data,
			description: data.description || undefined,
			meterThreshold: scheduleType === ScheduleType.MeterBased ? data.meterThreshold : undefined,
			currentMeterValue: scheduleType === ScheduleType.MeterBased ? data.currentMeterValue : undefined,
			workOrderTemplateId: data.workOrderTemplateId || undefined,
			assignedUserId: data.assignedUserId || undefined,
			defaultPriorityId: data.defaultPriorityId || undefined,
			defaultStatusId: data.defaultStatusId || undefined,
		};
		// Mark that we're submitting, so navigation blocking can be suppressed
		suppressBeforeUnloadRef.current = true;
		onSubmit(cleanedData);
	};

	return (
		<form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
			{error && (
				<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
					<p className="text-red-800 dark:text-red-200">{error}</p>
				</div>
			)}

			{/* Client Filter (for equipment selection) */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Filter by Client
				</label>
				<select
					value={clientFilter === 'all' ? 'all' : clientFilter}
					onChange={(e) => {
						const newClientFilter = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
						setClientFilter(newClientFilter);
						// Clear equipment selection when client filter changes
						if (newClientFilter !== 'all') {
							setValue('equipmentId', undefined);
						}
					}}
					className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
				>
					<option value="all">All Clients</option>
					{clients.map((client) => (
						<option key={client.id} value={client.id}>
							{client.name}
						</option>
					))}
				</select>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Filter equipment by client to make selection easier
				</p>
			</div>

			{/* Equipment Selection */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Equipment <span className="text-red-500">*</span>
				</label>
				<Controller
					name="equipmentId"
					control={control}
					render={({ field }) => (
						<select
							{...field}
							disabled={!!equipmentId}
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
						>
							<option value="">Select Equipment</option>
							{filteredEquipmentList.map((eq: EquipmentDto) => (
								<option key={eq.id} value={eq.id}>
									{eq.name} - {eq.clientLocationName} ({eq.clientName})
								</option>
							))}
						</select>
					)}
				/>
				{errors.equipmentId && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.equipmentId.message as string}</p>
				)}
				{clientFilter !== 'all' && filteredEquipmentList.length === 0 && (
					<p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
						No equipment found for the selected client
					</p>
				)}
			</div>

			{/* Schedule Name */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Schedule Name <span className="text-red-500">*</span>
				</label>
				<input
					type="text"
					{...register('name')}
					className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
					placeholder="e.g., Monthly HVAC Filter Replacement"
				/>
				{errors.name && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name.message as string}</p>
				)}
			</div>

			{/* Description */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Description
				</label>
				<textarea
					{...register('description')}
					rows={3}
					className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
					placeholder="Optional description of the maintenance schedule"
				/>
				{errors.description && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description.message as string}</p>
				)}
			</div>

			{/* Schedule Type */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Schedule Type <span className="text-red-500">*</span>
				</label>
				<Controller
					name="scheduleType"
					control={control}
					render={({ field }) => (
						<select
							{...field}
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
						>
							<option value={ScheduleType.Calendar}>Calendar-Based</option>
							<option value={ScheduleType.MeterBased}>Meter-Based</option>
						</select>
					)}
				/>
				{errors.scheduleType && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.scheduleType.message as string}</p>
				)}
			</div>

			{/* Frequency (Calendar-Based) */}
			{scheduleType === ScheduleType.Calendar && (
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Frequency Value <span className="text-red-500">*</span>
						</label>
						<input
							type="number"
							{...register('frequencyValue', { valueAsNumber: true })}
							min="1"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
						/>
						{errors.frequencyValue && (
							<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.frequencyValue.message as string}</p>
						)}
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Frequency Unit <span className="text-red-500">*</span>
						</label>
						<Controller
							name="frequencyUnit"
							control={control}
							render={({ field }) => (
								<select
									{...field}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
								>
									<option value={FrequencyUnit.Days}>Days</option>
									<option value={FrequencyUnit.Weeks}>Weeks</option>
									<option value={FrequencyUnit.Months}>Months</option>
									<option value={FrequencyUnit.Years}>Years</option>
								</select>
							)}
						/>
						{errors.frequencyUnit && (
							<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.frequencyUnit.message as string}</p>
						)}
					</div>
				</div>
			)}

			{/* Meter-Based Fields */}
			{scheduleType === ScheduleType.MeterBased && (
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Meter Threshold <span className="text-red-500">*</span>
						</label>
						<input
							type="number"
							{...register('meterThreshold', { valueAsNumber: true })}
							min="1"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
							placeholder="e.g., 10000"
						/>
						{errors.meterThreshold && (
							<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.meterThreshold.message as string}</p>
						)}
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Current Meter Value
						</label>
						<input
							type="number"
							{...register('currentMeterValue', { valueAsNumber: true })}
							min="0"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
							placeholder="Current reading"
						/>
						{errors.currentMeterValue && (
							<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.currentMeterValue.message as string}</p>
						)}
					</div>
				</div>
			)}

			{/* Next Due Date */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Next Due Date <span className="text-red-500">*</span>
				</label>
				<input
					type="date"
					{...register('nextDueDate')}
					className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
				/>
				{errors.nextDueDate && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nextDueDate.message as string}</p>
				)}
			</div>

			{/* Auto-Generate Work Orders */}
			<div className="flex items-center">
				<input
					type="checkbox"
					{...register('autoGenerateWorkOrders')}
					className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 rounded"
				/>
				<label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
					Automatically generate work orders when due
				</label>
			</div>

			{/* Work Order Template (if auto-generate is enabled) */}
			{autoGenerateWorkOrders && (
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Work Order Template
					</label>
					<Controller
						name="workOrderTemplateId"
						control={control}
						render={({ field }) => (
							<select
								{...field}
								value={field.value || ''}
								onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
							>
								<option value="">No Template</option>
								{templates.map((template: WorkOrderTemplate) => (
									<option key={template.id} value={template.id}>
										{template.name}
									</option>
								))}
							</select>
						)}
					/>
				</div>
			)}

			{/* Assigned User */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Assigned User
				</label>
				<Controller
					name="assignedUserId"
					control={control}
					render={({ field }) => (
						<select
							{...field}
							value={field.value || ''}
							onChange={(e) => field.onChange(e.target.value || undefined)}
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
						>
							<option value="">No Assignment</option>
							{users.map((user: User) => (
								<option key={user.id} value={user.id}>
									{user.firstName} {user.lastName} ({user.email})
								</option>
							))}
						</select>
					)}
				/>
			</div>

			{/* Default Priority */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Default Priority
				</label>
				<Controller
					name="defaultPriorityId"
					control={control}
					render={({ field }) => (
						<select
							{...field}
							value={field.value || ''}
							onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
						>
							<option value="">No Default</option>
							{priorities.map((priority: PriorityDto) => (
								<option key={priority.id} value={priority.id}>
									{priority.name}
								</option>
							))}
						</select>
					)}
				/>
			</div>

			{/* Default Status */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Default Status
				</label>
				<Controller
					name="defaultStatusId"
					control={control}
					render={({ field }) => (
						<select
							{...field}
							value={field.value || ''}
							onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
						>
							<option value="">No Default</option>
							{statuses.map((status: StatusDto) => (
								<option key={status.id} value={status.id}>
									{status.name}
								</option>
							))}
						</select>
					)}
				/>
			</div>

			{/* Lead Time */}
			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					Lead Time (Days) <span className="text-red-500">*</span>
				</label>
				<input
					type="number"
					{...register('leadTimeDays', { valueAsNumber: true })}
					min="0"
					max="365"
					className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
					placeholder="Days before due date to generate work order"
				/>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Work orders will be generated this many days before the due date
				</p>
				{errors.leadTimeDays && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.leadTimeDays.message as string}</p>
				)}
			</div>

			{/* Active Status */}
			<div className="flex items-center">
				<input
					type="checkbox"
					{...register('isActive')}
					className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 rounded"
				/>
				<label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
					Schedule is active
				</label>
			</div>

			{/* Submit Button */}
			<div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
				<button
					type="submit"
					disabled={isLoading}
					className="px-6 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isLoading ? 'Saving...' : schedule ? 'Update Schedule' : 'Create Schedule'}
				</button>
			</div>
		</form>
	);
};

export default MaintenanceScheduleForm;






